import { useEffect, useMemo, useState } from 'react';
import {
  ensureDisplayImageUrls,
  isManagedStorageUrl,
  isSignedViewEnabled,
  isUploadsStoragePath,
  resolveDisplayImageUrl,
} from '@/utils/imageDisplayAccess';
import { resolveFetchableImageUrl } from '@/utils/imageUrlUtils';

/** GoodsSharedContent / 任务列表同步预览 URL（需配合 prefetchWorkbenchPreviewUrls 预取） */
export function workbenchPreviewUrl(rawUrl, options = {}) {
  const trimmed = String(rawUrl || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('data:') || trimmed.startsWith('blob:')) return trimmed;
  const resolved = resolveDisplayImageUrl(trimmed, options);
  if (resolved) return resolved;
  if (isSignedViewEnabled() && isUploadsStoragePath(trimmed)) return '';
  return trimmed;
}

/** 无 sign-batch 缓存时自动异步签名（prefetch 兜底用） */
export async function workbenchPreviewUrlAsync(rawUrl, options = {}) {
  const sync = workbenchPreviewUrl(rawUrl, options);
  if (sync) return sync;
  return resolveFetchableImageUrl(rawUrl, options);
}

function normalizePreviewUrlList(rawUrls) {
  return [...new Set(
    (rawUrls || []).map((u) => String(u || '').trim()).filter(Boolean),
  )];
}

/**
 * 历史列表/任务表：先 batch sign，再对仍无缓存的受管 URL 逐条 async 签名。
 * 使 workbenchPreviewUrl 同步路径在 prefetch 后可用。
 */
export async function prefetchWorkbenchPreviewUrls(rawUrls, options = {}) {
  const urls = normalizePreviewUrlList(rawUrls);
  if (!urls.length) return;

  await ensureDisplayImageUrls(urls);

  const needAsync = urls.filter((u) => {
    if (u.startsWith('data:') || u.startsWith('blob:')) return false;
    if (!isManagedStorageUrl(u) || !isSignedViewEnabled()) return false;
    return !workbenchPreviewUrl(u, options);
  });

  if (!needAsync.length) return;

  await Promise.all(needAsync.map((u) => workbenchPreviewUrlAsync(u, options)));
}

/**
 * 历史面板：预取当前页预览 URL，完成后 bump tick 以刷新 sync 预览。
 * @returns {number} tick — 依赖此值可使 getTaskPreviewUrl 在 prefetch 后重算
 */
export function useWorkbenchPreviewPrefetch(rawUrls, { enabled = true } = {}) {
  const [tick, setTick] = useState(0);
  const urlKey = useMemo(
    () => normalizePreviewUrlList(rawUrls).sort().join('\0'),
    [rawUrls],
  );

  useEffect(() => {
    if (!enabled || !urlKey) return undefined;
    let alive = true;
    void prefetchWorkbenchPreviewUrls(urlKey.split('\0')).then(() => {
      if (alive) setTick((n) => n + 1);
    });
    return () => {
      alive = false;
    };
  }, [urlKey, enabled]);

  return tick;
}
